import React from 'react';
import style from './sale-banner.module.scss';
import { Box, Button, Text } from '@wix/design-system';
import { Countdown } from './countdown';
import type { ExternalComponentProps } from '../types';
import { useRemainingTime } from './hooks/use-remaining-time';
import {
  useEssentials,
  withEssentialsContext,
} from '../../contexts/essentials-context';
import { useDateFormatter } from './hooks/use-date-formatter';
import { Trans } from '@wix/wix-i18n-config';
import { SaleEndDate } from './constants/sale-end-date';

export type SaleBannerProps = {
  layout?: 'narrow' | 'wide';
  onClick?: () => void;
} & ExternalComponentProps;

function SaleBannerComponent({ layout = 'narrow', onClick }: SaleBannerProps) {
  const remainingTime = useRemainingTime(SaleEndDate);

  if (!remainingTime) {
    return null;
  }

  return (
    <div className={style.saleBannerContainer}>
      <Box
        dataHook="sale-banner"
        className={`${style.banner} ${style[`${layout}Layout`]} ${
          style.withCta
        }`}
        direction="vertical"
      >
        <div className={style.bannerBackground} />
        <Box className={style.bannerContent} align="space-between">
          <Box className={style.bannerLeftSection}>
            <Box direction="vertical">
              <Box className={style.bannerHeadingGroup}>
                <BannerEyeBrowTitle />
                <BannerTitle />
              </Box>
              <Box gap="12px" verticalAlign="middle">
                {onClick && (
                  <Box>
                    <BannerCTA onClick={onClick} />
                  </Box>
                )}
                <Box className={style.bannerLeftSection}>
                  <BannerDisclaimer />
                </Box>
              </Box>
            </Box>
          </Box>
          <Box className={style.bannerRightSection}>
            <Box className={style.countdownContainer} verticalAlign="middle">
              <Countdown
                size={layout === 'narrow' ? 'large' : 'small'}
                days={remainingTime.days}
                hours={remainingTime.hours}
                minutes={remainingTime.minutes}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </div>
  );
}

function BannerTitle() {
  const { t } = useEssentials();
  return <Text className={style.bannerTitle}>{t('sale.banner.title')}</Text>;
}

function BannerEyeBrowTitle() {
  const { t, i18n } = useEssentials();

  return (
    <Text className={style.bannerEyeBrowTitle}>
      <Trans
        t={t}
        i18n={i18n}
        i18nKey="sale.banner.eyeBrowTitle"
        components={[<span />]}
      />
    </Text>
  );
}

function BannerCTA({ onClick }: { onClick: () => void }) {
  const { t } = useEssentials();
  return (
    <Box>
      <Button
        skin="dark"
        dataHook="sale-banner-cta"
        onClick={onClick}
        className={style.bannerCta}
        size="small"
      >
        {t('sale.banner.cta.label')}
      </Button>
    </Box>
  );
}

function BannerDisclaimer() {
  const { t } = useEssentials();
  const dateFormatter = useDateFormatter();

  const fixForSSR = (dateString: string) =>
    dateString.replace('24:00', '00:00');

  return (
    <Text className={style.bannerDisclaimer} light size="tiny" weight="normal">
      {t('sale.banner.disclaimer', {
        date: fixForSSR(dateFormatter(SaleEndDate)),
      })}
    </Text>
  );
}

export const SaleBanner = withEssentialsContext(SaleBannerComponent);
